<template>
  <div class="flex flex-row gap-y-1" v-show="isShow">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="relative flex items-center gap-x-2"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col justify-start items-start gap-2">
        <DataTable :data-set="processedDataTable" v-if="processedDataTable" class="w-full">
          <template #cell="{ columnName, columnValue, rowIndex }">
            <span v-if="needHtmlFormatColumn.includes(columnName)" v-html="columnValue"></span>
            <div class="min-h-4" v-else>{{ columnValue }}</div>
          </template>
          <template #tableFooter>
            <tr
              v-for="(row, key) in formatAppendRow"
              class="odd:bg-brand-gray-100 ltr:font-sans text-xs"
            >
              <td
                v-for="(col, key) in row"
                :colspan="col?.config?.colspan"
                class="p-1 ps-2 border border-gray-100 ltr:font-sans text-xs"
              >
                {{ col.label }}
              </td>
            </tr>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeMount, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormStore } from '@/stores/form'
import useVisible from '@/composables/useVisible'

import useRefundAmountGroupActions from '@/composables/actions/useRefundAmountGroupActions.js'
import useRefundAmountGroupFormat from '@/composables/actions/useRefundAmountGroupFormat.js'
import { useRoute } from 'vue-router'
import DataTable from '@/components/DataTable/index.vue'
import HintTippy from '@/components/Form/HintTyppy.vue'
import lodashGet from 'lodash/get'
import { useField } from 'vee-validate'

import { useAuthStore } from '@/stores/auth'
import { useLookupsStore } from '@/stores/lookups'

const props = defineProps({
  addressType: String,
  appendRow: {
    type: Array
  },
  visibleColumns: {
    type: Array,
    required: true
  },
  layout: {
    type: String,
    default: 'one-column'
  },
  hint: String,
  labelValue: String,
  radioOptions: {
    type: Array,
    required: true
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: [String, Number]
  },
  rules: Array,
  showRules: Object,
  defaultValue: String,
  direction: {
    type: String,
    default: 'flex-row md:items-start '
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String,
  sequenceOptions: Array,
  options: {
    type: Array,
    required: true
  }
})
const route = useRoute()
const formStore = useFormStore()
const i18n = useI18n()
const lookupStore = useLookupsStore()

const needHtmlFormatColumn = ref([])
const processedDataTable = ref([])
const formatAppendRow = ref()

const fieldName = toRef(props, 'fieldName')

const { errorMessage, handleChange, handleBlur, meta } = useField(fieldName)

// Process value from dynamic fields, then pass to query of the api.
const processQuery = (query) => {
  if (!query) return
  const newQuery = {}
  if (Object.keys(query).length > 0) {
    Object.keys(query).map((key) => {
      if (query[key].ref) {
        newQuery[key] = formStore.extraMap[query[key].ref] || formStore.form.values[query[key].ref]
      } else {
        newQuery[key] = query[key]
      }
    })
  }
  return newQuery
}
// Process value from dynamic fields, then replace the patch with these.
const processPathQuery = (queryPath) => {
  if (!queryPath) return
  let newQueryPath = ''
  if (Object.keys(queryPath).length > 0) {
    Object.keys(queryPath).map((key) => {
      if (queryPath[key].ref) {
        newQueryPath += `/${formStore.form.values[queryPath[key].ref] || formStore.extraMap[queryPath[key].ref]}`
      } else {
        newQueryPath += queryPath[key]
      }
    })
  }
  return newQueryPath
}

const { findShowRule, getBindObj } = useVisible()
const refundAmountGroupFormat = useRefundAmountGroupFormat()

const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
const isShow = computed(() => {
  if (props.showRules) {
    return findShowRule(
      props.showRules.any || props.showRules.all,
      props.showRules.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
  } else {
    return true
  }
})
const t9nLabel = computed(() => i18n.t(props.label))

const formateTable = (item) => {
  const resMap = {}

  props.visibleColumns.map((column) => {
    if (column.format) {
      return (resMap[i18n.t(column.label)] = refundAmountGroupFormat[column.format](
        lodashGet(item, column.value),
        column
      ))
    }
    return (resMap[i18n.t(column.label)] = lodashGet(item, column.value))
  })

  return resMap
}

const fetchData = async () => {
  const { path, query, body } = props.options.api
  let newPath = path
  if (formStore.form.values[props.dependentOn] && newPath.includes('__reactive__')) {
    newPath = newPath.replaceAll('__reactive__', formStore.form.values[props.dependentOn])
  }

  let options = []
  // special case for professions
  if (props.options.api.path_query) {
    newPath += processPathQuery(props.options.api.path_query)
  }
  const newQuery = processQuery(query)
  const newData = processQuery(body)
  try {
    options = await lookupStore.fetchLookup(
      newPath,
      newQuery,
      {},
      props.options.api.needProcess,
      props.options.api.method,
      newData,
      props.options.api.need_cache
    )
  } catch (error) {
    console.error('fetchLookup RefundAmountGroup', error)
  }

  handleChange(options)

  /**
   * sometimes, extra logic needs to be made.
   * one example is service 767. On change of Person Category,
   * Person Sub Category is fetched, but needs extra handlers to filter out
   * options based on user type, and other factors.
   */
  if (props.options.api.on_success?.action) {
    const { action, action_params } = props.options.api.on_success
    options = await useRefundAmountGroupActions()[action]({
      ...action_params,
      options
    })
  }

  processedDataTable.value = options.map(formateTable)

  formatAppendRow.value = props?.appendRow?.map((row) =>
    row.map((col) => {
      return {
        ...col,
        label: col.label ? i18n.t(col.label) : formStore.extraMap[col.value]
      }
    })
  )
}
watch(
  () => lodashGet(formStore.form.values, props.dependentOn),
  async (newValue) => {
    if (newValue.length >= 25) {
      await fetchData()
    }
  },
  { immediate: true }
)
</script>
<script>
export default {
  name: 'RefundAmountGroup'
}
</script>

<style lang="scss" scoped></style>
